import {
  parseLoomWingBodyDefinitionV2,
  type LoomV3Profile,
  type LoomV3Section,
  type LoomWingBodyDefinitionV2,
} from "../../api/loomRebuildV3";
import { canonicalLoomDefinitionJson } from "./loomAcceptedDefinitionBinding";
import type {
  LoomAirfoil,
  LoomAuthoringDocument,
  LoomFuselageStation,
  LoomSceneNode,
  LoomWingPanel,
} from "./loomAuthoringModel";
import {
  parseLoomPresetReceipt,
  type LoomPresetReceipt,
} from "./loomPresetReceipt";

export const LOOM_V3_PRESET_GRAPH_IDS = {
  fuselage: "fuselage",
  partner: "wing-partner",
  placement: "wing-placement",
  rotateX: "wing-rotate-x",
  rotateY: "wing-rotate-y",
  rotateZ: "wing-rotate-z",
  vehicle: "vehicle",
  wing: "wing",
} as const;

type JsonScalar = boolean | number | string | null;
export type LoomPresetQcJson = JsonScalar | readonly LoomPresetQcJson[] | {
  readonly [key: string]: LoomPresetQcJson;
};

export interface LoomPresetV3Binding {
  schema: "loom.preset-binding/v1";
  acceptedAssetSha256: `sha256:${string}`;
  acceptedMeshSchema: "loom.accepted-mesh/v3";
  definition: LoomWingBodyDefinitionV2;
  evaluatorId: "loom.native-wing-body/v3";
  presetReceipt: LoomPresetReceipt;
  qcReceipt: Readonly<Record<string, LoomPresetQcJson>>;
  topology: "selected-wing-fuselage-pairwise-union";
  unionAlgorithmId: "loom.implicit-guided-explicit-union/v1";
}

type UnknownRecord = Record<string, unknown>;
type PrimitiveNode = Extract<LoomSceneNode, { type: "primitive" }>;

function record(value: unknown, label: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as UnknownRecord;
}

function exact(value: unknown, keys: readonly string[], label: string): UnknownRecord {
  const item = record(value, label);
  const actual = Object.keys(item);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
  return item;
}

function sha256(value: unknown, label: string, prefix = true): string {
  if (typeof value !== "string"
      || !(prefix ? /^sha256:[a-f0-9]{64}$/ : /^(?:sha256:)?[a-f0-9]{64}$/).test(value)) {
    throw new Error(`${label} must be a lowercase SHA-256 identity`);
  }
  return value;
}

function finiteJson(value: unknown, label: string, depth = 0): LoomPresetQcJson {
  if (depth > 12) throw new Error(`${label} is too deeply nested`);
  if (value === null || typeof value === "boolean" || typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error(`${label} contains a non-finite number`);
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length > 1024) throw new Error(`${label} exceeds its array budget`);
    return value.map((item, index) => finiteJson(item, `${label}[${index}]`, depth + 1));
  }
  const item = record(value, label);
  if (Object.keys(item).length > 128) throw new Error(`${label} exceeds its field budget`);
  return Object.fromEntries(Object.entries(item).map(([key, child]) => [
    key, finiteJson(child, `${label}.${key}`, depth + 1),
  ]));
}

function deepFreeze<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value as UnknownRecord).forEach(deepFreeze);
  return Object.freeze(value);
}

function same(left: unknown, right: unknown): boolean {
  return canonicalLoomDefinitionJson(left) === canonicalLoomDefinitionJson(right);
}

function parseQcReceipt(
  value: unknown,
  definitionRevision: string,
  acceptedAssetSha256: string,
): Readonly<Record<string, LoomPresetQcJson>> {
  const keys = [
    "status", "passed", "evaluatorId", "definitionRevision", "acceptedAssetSha256",
    "surfaceVertexCount", "surfaceFaceCount", "panelVertexCount", "panelFaceCount", "volumeM3",
    "seamMaxCoordinateGapM", "resolution", "surfaceQuality", "panelQuality",
  ];
  const qc = exact(value, keys, "presetBinding.qcReceipt");
  const surface = record(qc.surfaceQuality, "presetBinding.qcReceipt.surfaceQuality");
  const panel = record(qc.panelQuality, "presetBinding.qcReceipt.panelQuality");
  const qcAsset = sha256(qc.acceptedAssetSha256, "presetBinding.qcReceipt.acceptedAssetSha256", false);
  if (qc.status !== "passed" || qc.passed !== true
      || qc.evaluatorId !== "loom.native-wing-body/v3"
      || qc.definitionRevision !== definitionRevision
      || qcAsset.replace(/^sha256:/, "") !== acceptedAssetSha256.replace(/^sha256:/, "")
      || surface.passed !== true || panel.passed !== true) {
    throw new Error("presetBinding QC is not passed and bound to the v3 definition/asset");
  }
  const parsed = finiteJson(qc, "presetBinding.qcReceipt");
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("presetBinding.qcReceipt is malformed");
  }
  return parsed as Readonly<Record<string, LoomPresetQcJson>>;
}

export function parseLoomPresetV3Binding(value: unknown): LoomPresetV3Binding {
  const item = exact(value, [
    "schema", "acceptedAssetSha256", "acceptedMeshSchema", "definition", "evaluatorId",
    "presetReceipt", "qcReceipt", "topology", "unionAlgorithmId",
  ], "presetBinding");
  if (item.schema !== "loom.preset-binding/v1"
      || item.acceptedMeshSchema !== "loom.accepted-mesh/v3"
      || item.evaluatorId !== "loom.native-wing-body/v3"
      || item.topology !== "selected-wing-fuselage-pairwise-union"
      || item.unionAlgorithmId !== "loom.implicit-guided-explicit-union/v1") {
    throw new Error("presetBinding v3 contract discriminator is unsupported");
  }
  const presetReceipt = parseLoomPresetReceipt(item.presetReceipt);
  const definition = parseLoomWingBodyDefinitionV2(item.definition);
  const acceptedAssetSha256 = sha256(
    item.acceptedAssetSha256, "presetBinding.acceptedAssetSha256",
  ) as `sha256:${string}`;
  const source = definition.sourceBinding;
  if (source.sourceAssetSha256.replace(/^sha256:/, "")
        !== presetReceipt.sourceAssetSha256.replace(/^sha256:/, "")
      || !same(source.selectedComponents, presetReceipt.selectedComponents)
      || !same(source.normalization, presetReceipt.normalization)) {
    throw new Error("presetBinding source facts do not match its immutable preset receipt");
  }
  return deepFreeze({
    schema: item.schema,
    acceptedAssetSha256,
    acceptedMeshSchema: item.acceptedMeshSchema,
    definition,
    evaluatorId: item.evaluatorId,
    presetReceipt,
    qcReceipt: parseQcReceipt(
      item.qcReceipt, presetReceipt.definitionRevision, acceptedAssetSha256,
    ),
    topology: item.topology,
    unionAlgorithmId: item.unionAlgorithmId,
  });
}

function sectionToAirfoil(section: LoomV3Section): LoomAirfoil {
  if (section.family === "naca4") return {
    family: "naca4", camberRatio: section.camberRatio,
    camberLocationRatio: section.camberLocationRatio,
    thicknessRatio: section.thicknessRatio,
    closedTrailingEdge: section.closedTrailingEdge,
    trailingEdgeThicknessRatio: section.trailingEdgeThicknessRatio,
  };
  if (section.family === "cst") return {
    family: "cst",
    upperCoefficients: [...section.upperCoefficients],
    lowerCoefficients: [...section.lowerCoefficients],
  };
  return {
    family: "polygon",
    vertices: section.vertices.map((vertex) => [...vertex] as [number, number]),
    closedTrailingEdge: section.closedTrailingEdge,
    trailingEdgeThicknessRatio: section.trailingEdgeThicknessRatio,
  };
}

function stationFromProfile(
  station: LoomWingBodyDefinitionV2["fuselage"]["stations"][number],
  profiles: Readonly<Record<string, LoomV3Profile>>,
): LoomFuselageStation {
  if (station.kind === "point") return { ...station };
  const profile = profiles[station.profileRef];
  if (!profile || profile.kind !== "superellipse") {
    throw new Error("v3 polygon fuselage profiles are not faithfully authorable yet");
  }
  return {
    stationId: station.stationId,
    kind: "superellipse",
    xM: station.xM,
    widthM: profile.widthM,
    heightM: profile.heightM,
    widthExponent: profile.widthExponent,
    heightExponent: profile.heightExponent,
    zCenterM: profile.zCenterM,
  };
}

export function createLoomPresetV3Replacement(
  definitionValue: unknown,
): { nodes: readonly LoomSceneNode[]; rootNodeIds: readonly string[] } {
  const definition = parseLoomWingBodyDefinitionV2(definitionValue);
  const rootSection = definition.sectionDefinitions[definition.wing.panel.airfoilRootRef];
  const tipSection = definition.sectionDefinitions[definition.wing.panel.airfoilTipRef];
  if (!rootSection || !tipSection) throw new Error("v3 wing section references are missing");
  const { airfoilRootRef: _root, airfoilTipRef: _tip, ...panel } = definition.wing.panel;
  const nodes: LoomSceneNode[] = [
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.wing,
      label: "Selected source wing derivative",
      type: "primitive",
      definition: {
        primitive: "wing", panels: [{
          ...panel,
          airfoilRoot: sectionToAirfoil(rootSection),
          airfoilTip: sectionToAirfoil(tipSection),
        }], tip: "flat", tipRadiusM: 0,
      },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.fuselage,
      label: "Selected source body derivative",
      type: "primitive",
      definition: {
        primitive: "fuselage",
        stations: definition.fuselage.stations.map((station) => (
          stationFromProfile(station, definition.profileDefinitions)
        )),
      },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.rotateX,
      label: "Selected wing X rotation",
      type: "transform",
      inputNodeId: LOOM_V3_PRESET_GRAPH_IDS.wing,
      operation: {
        transform: "rotate", axis: "x",
        angleDeg: definition.wing.placement.rotationDegXYZ[0], originM: [0, 0, 0],
      },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.rotateY,
      label: "Selected wing Y rotation",
      type: "transform",
      inputNodeId: LOOM_V3_PRESET_GRAPH_IDS.rotateX,
      operation: {
        transform: "rotate", axis: "y",
        angleDeg: definition.wing.placement.rotationDegXYZ[1], originM: [0, 0, 0],
      },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.rotateZ,
      label: "Selected wing Z rotation",
      type: "transform",
      inputNodeId: LOOM_V3_PRESET_GRAPH_IDS.rotateY,
      operation: {
        transform: "rotate", axis: "z",
        angleDeg: definition.wing.placement.rotationDegXYZ[2], originM: [0, 0, 0],
      },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.placement,
      label: "Selected wing translation",
      type: "transform",
      inputNodeId: LOOM_V3_PRESET_GRAPH_IDS.rotateZ,
      operation: { transform: "translate", offsetM: [...definition.wing.placement.offsetM] },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.partner,
      label: "Derived mirrored wing partner",
      type: "transform",
      inputNodeId: LOOM_V3_PRESET_GRAPH_IDS.placement,
      operation: { transform: "mirror", axis: "y", planeOffsetM: 0 },
    },
    {
      id: LOOM_V3_PRESET_GRAPH_IDS.vehicle,
      label: "Selected wing/body derivative",
      type: "boolean",
      operation: "union",
      inputNodeIds: [
        LOOM_V3_PRESET_GRAPH_IDS.fuselage,
        LOOM_V3_PRESET_GRAPH_IDS.placement,
        LOOM_V3_PRESET_GRAPH_IDS.partner,
      ],
    },
  ];
  return { nodes, rootNodeIds: [LOOM_V3_PRESET_GRAPH_IDS.vehicle] };
}

function graphParts(nodes: readonly LoomSceneNode[], rootNodeIds: readonly string[]) {
  const ids = LOOM_V3_PRESET_GRAPH_IDS;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const wing = byId.get(ids.wing);
  const fuselage = byId.get(ids.fuselage);
  const rotateX = byId.get(ids.rotateX);
  const rotateY = byId.get(ids.rotateY);
  const rotateZ = byId.get(ids.rotateZ);
  const placement = byId.get(ids.placement);
  const partner = byId.get(ids.partner);
  const vehicle = byId.get(ids.vehicle);
  if (nodes.length !== 8 || rootNodeIds.length !== 1 || rootNodeIds[0] !== ids.vehicle
      || wing?.type !== "primitive" || wing.definition.primitive !== "wing"
      || fuselage?.type !== "primitive" || fuselage.definition.primitive !== "fuselage"
      || rotateX?.type !== "transform" || rotateX.inputNodeId !== ids.wing
      || rotateX.operation.transform !== "rotate" || rotateX.operation.axis !== "x"
      || !same(rotateX.operation.originM, [0, 0, 0])
      || rotateY?.type !== "transform" || rotateY.inputNodeId !== ids.rotateX
      || rotateY.operation.transform !== "rotate" || rotateY.operation.axis !== "y"
      || !same(rotateY.operation.originM, [0, 0, 0])
      || rotateZ?.type !== "transform" || rotateZ.inputNodeId !== ids.rotateY
      || rotateZ.operation.transform !== "rotate" || rotateZ.operation.axis !== "z"
      || !same(rotateZ.operation.originM, [0, 0, 0])
      || placement?.type !== "transform" || placement.inputNodeId !== ids.rotateZ
      || placement.operation.transform !== "translate"
      || partner?.type !== "transform" || partner.inputNodeId !== ids.placement
      || partner.operation.transform !== "mirror" || partner.operation.axis !== "y"
      || partner.operation.planeOffsetM !== 0
      || vehicle?.type !== "boolean" || vehicle.operation !== "union"
      || !same(vehicle.inputNodeIds, [ids.fuselage, ids.placement, ids.partner])) {
    throw new Error("presetBinding graph ownership or stable v3 adapter topology changed");
  }
  return {
    fuselage: fuselage as PrimitiveNode,
    placement: placement as Extract<LoomSceneNode, { type: "transform" }>,
    rotateX: rotateX as Extract<LoomSceneNode, { type: "transform" }>,
    rotateY: rotateY as Extract<LoomSceneNode, { type: "transform" }>,
    rotateZ: rotateZ as Extract<LoomSceneNode, { type: "transform" }>,
    wing: wing as PrimitiveNode,
  };
}

function sectionFromAirfoil(airfoil: LoomAirfoil, fallback: LoomV3Section): LoomV3Section {
  const sourceTrailingEdge = { ...fallback.sourceTrailingEdge };
  if (airfoil.family === "naca4") return { ...airfoil, sourceTrailingEdge };
  if (airfoil.family === "cst") return {
    family: "cst",
    upperCoefficients: [...airfoil.upperCoefficients],
    lowerCoefficients: [...airfoil.lowerCoefficients],
    trailingEdgeThicknessRatio: fallback.family === "cst"
      ? fallback.trailingEdgeThicknessRatio : 0.01,
    sourceTrailingEdge,
  };
  return {
    family: "polygon",
    vertices: airfoil.vertices.map((vertex) => [...vertex] as [number, number]),
    closedTrailingEdge: airfoil.closedTrailingEdge,
    trailingEdgeThicknessRatio: airfoil.trailingEdgeThicknessRatio,
    sourceTrailingEdge,
  };
}

function uniqueRef(base: string, suffix: string): string {
  const tail = `-${suffix}`;
  return `${base.slice(0, Math.max(1, 160 - tail.length))}${tail}`;
}

function derivedSections(
  panel: LoomWingPanel,
  source: LoomWingBodyDefinitionV2,
): { definitions: Record<string, LoomV3Section>; rootRef: string; tipRef: string } {
  const originalRootRef = source.wing.panel.airfoilRootRef;
  const originalTipRef = source.wing.panel.airfoilTipRef;
  const originalRoot = source.sectionDefinitions[originalRootRef]!;
  const originalTip = source.sectionDefinitions[originalTipRef]!;
  const root = sectionFromAirfoil(panel.airfoilRoot, originalRoot);
  const tip = sectionFromAirfoil(panel.airfoilTip, originalTip);
  if (same(root, tip)) return { definitions: { [originalRootRef]: root }, rootRef: originalRootRef, tipRef: originalRootRef };
  const tipRef = originalTipRef === originalRootRef
    ? uniqueRef(originalTipRef, "tip") : originalTipRef;
  return {
    definitions: { [originalRootRef]: root, [tipRef]: tip },
    rootRef: originalRootRef,
    tipRef,
  };
}

function derivedProfiles(
  stations: readonly LoomFuselageStation[],
  source: LoomWingBodyDefinitionV2,
): {
  definitions: Record<string, LoomV3Profile>;
  stations: LoomWingBodyDefinitionV2["fuselage"]["stations"];
} {
  const sourceById = new Map(source.fuselage.stations.map((station) => [station.stationId, station]));
  const definitions: Record<string, LoomV3Profile> = {};
  const signatureRefs = new Map<string, string>();
  const derived = stations.map((station) => {
    const original = sourceById.get(station.stationId);
    if (!original || (original.kind === "point") !== (station.kind === "point")) {
      throw new Error("presetBinding fuselage station ownership or endpoint kind changed");
    }
    if (station.kind === "point" && original.kind === "point") return { ...station };
    if (station.kind !== "superellipse" || original.kind !== "profile") {
      throw new Error("presetBinding graph contains an unrepresentable fuselage profile");
    }
    const profile: LoomV3Profile = {
      kind: "superellipse",
      widthM: station.widthM,
      heightM: station.heightM,
      widthExponent: station.widthExponent,
      heightExponent: station.heightExponent,
      zCenterM: station.zCenterM,
    };
    const signature = canonicalLoomDefinitionJson(profile);
    let profileRef = signatureRefs.get(signature);
    if (!profileRef) {
      const preferred = original.profileRef;
      profileRef = definitions[preferred] === undefined
        ? preferred : uniqueRef(preferred, station.stationId);
      definitions[profileRef] = profile;
      signatureRefs.set(signature, profileRef);
    }
    return { stationId: station.stationId, kind: "profile" as const, xM: station.xM, profileRef };
  });
  return { definitions, stations: derived };
}

function deriveFromGraph(
  nodes: readonly LoomSceneNode[],
  rootNodeIds: readonly string[],
  binding: LoomPresetV3Binding,
): LoomWingBodyDefinitionV2 {
  const parts = graphParts(nodes, rootNodeIds);
  if (parts.wing.definition.primitive !== "wing"
      || parts.fuselage.definition.primitive !== "fuselage"
      || parts.wing.definition.panels.length !== 1
      || parts.wing.definition.tip !== "flat" || parts.wing.definition.tipRadiusM !== 0
      || parts.rotateX.operation.transform !== "rotate"
      || parts.rotateY.operation.transform !== "rotate"
      || parts.rotateZ.operation.transform !== "rotate"
      || parts.placement.operation.transform !== "translate") {
    throw new Error("presetBinding graph is outside the v3 wing/body adapter");
  }
  const panel = parts.wing.definition.panels[0]!;
  const source = binding.definition;
  const sections = derivedSections(panel, source);
  const profiles = derivedProfiles(parts.fuselage.definition.stations, source);
  const {
    airfoilRoot: _airfoilRoot, airfoilTip: _airfoilTip,
    sectionRecipes: _sectionRecipes, ...panelScalars
  } = panel;
  return parseLoomWingBodyDefinitionV2({
    ...source,
    sectionDefinitions: sections.definitions,
    profileDefinitions: profiles.definitions,
    wing: {
      ...source.wing,
      panel: {
        ...panelScalars,
        airfoilRootRef: sections.rootRef,
        airfoilTipRef: sections.tipRef,
      },
      placement: {
        ...source.wing.placement,
        offsetM: [...parts.placement.operation.offsetM],
        rotationDegXYZ: [
          parts.rotateX.operation.angleDeg,
          parts.rotateY.operation.angleDeg,
          parts.rotateZ.operation.angleDeg,
        ],
      },
    },
    fuselage: { ...source.fuselage, stations: profiles.stations },
  });
}

export function assertLoomPresetV3GraphBinding(
  nodes: readonly LoomSceneNode[],
  rootNodeIds: readonly string[],
  binding: LoomPresetV3Binding,
  receipt: LoomPresetReceipt,
): void {
  if (!same(receipt, binding.presetReceipt)
      || !same(deriveFromGraph(nodes, rootNodeIds, binding), binding.definition)) {
    throw new Error("preset.apply graph, receipt, and v3 authoritative binding disagree");
  }
}

export function deriveLoomPresetV3Definition(
  document: LoomAuthoringDocument,
): LoomWingBodyDefinitionV2 | null {
  if (!document.presetBinding) return null;
  const binding = parseLoomPresetV3Binding(document.presetBinding);
  return deriveFromGraph(document.nodes, document.rootNodeIds, binding);
}
